import { ShotPlanView } from './ShotPlan';
import { useState } from 'react';
import { Badge, Dialog, Field } from './shared';
import { type Asset, type StudioTask } from './production';
import { type Schema } from '../api/client';

type Reference = { name: string; asset: Asset };
export type KeyframeResult = {
  document: Schema<'ProductionDraft'>;
  consistencyReports?: Parameters<typeof ConsistencyReports>[0]['reports'];
  items: { shotId: string; asset: Asset; references: Reference[]; visualPrompt?: string }[];
};
export type KeyframePreview = {
  stepId: string;
  shotId: string;
  ordinal: number;
  state: string;
  asset?: Asset;
  errorMessage?: string;
};

export function KeyframeImage({ asset, label }: { asset: Asset; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="keyframe-image-button"
        aria-label={`放大${label}`}
        onClick={() => setOpen(true)}
      >
        <img className="keyframe-main" src={asset.url} alt={label} loading="lazy" />
      </button>
      {open && (
        <Dialog title={label} close={() => setOpen(false)}>
          <div className="dialog-body keyframe-lightbox">
            <img src={asset.url} alt={`${label}大图`} />
          </div>
          <div className="dialog-actions">
            <a className="button secondary" href={asset.url} target="_blank" rel="noreferrer">
              查看原图
            </a>
            <button className="button primary" onClick={() => setOpen(false)}>
              关闭预览
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

export function KeyframeGallery({ items }: { items: KeyframePreview[] }) {
  return (
    <section className="novel-candidate keyframe-gallery">
      <div className="section-heading">
        <h2>关键帧预览</h2>
        <Badge>
          已生成 {items.filter((item) => item.asset?.url).length} / {items.length}
        </Badge>
      </div>
      <p className="quiet">
        生成一张即可查看，点击图片放大。流程暂停或某一镜失败时，已生成图片仍会保留。
      </p>
      <div className="keyframe-gallery-grid">
        {items.map((item) => (
          <article className="keyframe-preview-card" key={item.stepId}>
            <div className="section-heading">
              <h3>第 {item.ordinal + 1} 镜</h3>
              <Badge>
                {item.asset?.url
                  ? '已生成'
                  : item.state === 'failed'
                    ? '生成失败'
                    : item.state === 'working'
                      ? '生成中'
                      : '等待生成'}
              </Badge>
            </div>
            {item.asset?.url ? (
              <KeyframeImage asset={item.asset} label={`第 ${item.ordinal + 1} 镜关键帧`} />
            ) : (
              <div className="keyframe-placeholder">
                {item.state === 'failed'
                  ? '本次未生成可用图片'
                  : item.state === 'working'
                    ? '图片生成后会自动显示'
                    : '尚未开始生成'}
              </div>
            )}
            {item.errorMessage && <p className="keyframe-preview-error">{item.errorMessage}</p>}
          </article>
        ))}
      </div>
    </section>
  );
}

function References({ items }: { items: Reference[] }) {
  return (
    <div className="keyframe-references">
      {items.map((r) => (
        <figure key={r.asset.id}>
          <a href={r.asset.url} target="_blank" rel="noreferrer">
            <img src={r.asset.url} alt={r.name + ' 对照图'} loading="lazy" />
          </a>
          <figcaption>{r.name}</figcaption>
        </figure>
      ))}
    </div>
  );
}

export function KeyframeReview({
  result,
  busy,
  regenerate,
  editPrompt,
}: {
  result: KeyframeResult;
  busy: boolean;
  regenerate?: (shotId: string, feedback: string) => void;
  editPrompt?: (shotId: string) => void;
}) {
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  return (
    <div className="keyframe-review">
      <p className="info-box">
        逐镜核对出场人数、角色身份、人物站位和道具。检查报告是模型意见，仍需对照图片审阅。批准整组关键帧后，才会将每张图作为对应视频的首帧，并携带角色定妆图及你确认的画面修改生成视频。定妆图与文字造型有冲突时，以你确认的图为准。
      </p>
      {!!result.consistencyReports?.length && (
        <ConsistencyReports reports={result.consistencyReports} document={result.document} />
      )}
      {result.items.map((item, index) => {
        const shot = result.document.shots.find((s) => s.id === item.shotId);
        return (
          <section className="keyframe-shot" key={item.shotId}>
            <h3>镜头 {index + 1} · 起始关键帧</h3>
            <div className="keyframe-comparison">
              <figure>
                <KeyframeImage asset={item.asset} label={`第 ${index + 1} 镜待审批关键帧`} />
                <figcaption>待审批关键帧 · 点击放大</figcaption>
              </figure>
              <div>
                <strong>
                  {item.references.length ? '本次实际使用的定妆图' : '本镜头没有出场角色'}
                </strong>
                <References items={item.references} />
                {item.visualPrompt && (
                  <details>
                    <summary>本帧修改稿 · 审批后传给视频</summary>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{item.visualPrompt}</p>
                  </details>
                )}
                {shot && <ShotPlanView shot={shot} design={result.document} />}
              </div>
            </div>
            {editPrompt ? (
              <button
                className="button secondary"
                disabled={busy}
                onClick={() => editPrompt(item.shotId)}
              >
                编辑提示词并重做此帧
              </button>
            ) : (
              regenerate && (
                <div className="novel-feedback">
                  <Field label="本镜头修改意见">
                    <textarea
                      aria-label={`镜头 ${index + 1} 修改意见`}
                      maxLength={2000}
                      value={feedback[item.shotId] ?? ''}
                      onChange={(e) => setFeedback({ ...feedback, [item.shotId]: e.target.value })}
                      placeholder="例如：保持定妆图中的发型与衣服，只调整人物站位。"
                    />
                  </Field>
                  <button
                    className="button secondary"
                    disabled={busy}
                    onClick={() => regenerate(item.shotId, feedback[item.shotId] ?? '')}
                  >
                    重新生成此帧，保留其他候选
                  </button>
                </div>
              )
            )}
          </section>
        );
      })}
    </div>
  );
}

export function KeyframePromptEditor({
  number,
  original,
  busy,
  paused,
  error,
  close,
  submit,
}: {
  number: number;
  original: string;
  busy: boolean;
  paused: boolean;
  error: string;
  close: () => void;
  submit: (prompt: string) => Promise<void>;
}) {
  const [prompt, setPrompt] = useState(original);
  const changed = prompt.trim() !== original.trim();
  return (
    <Dialog title={`修改第 ${number} 镜关键帧提示词`} close={close}>
      <div className="dialog-body keyframe-prompt-editor">
        <p>
          修改后的全文会替换此帧原提示词。已确认的角色参考图仍会带给模型，其他已成功的关键帧会保留。
        </p>
        <Field
          label="关键帧提示词"
          hint="若被内容审核拦截，请修改不合适的画面和动作，而不是只在原文后追加要求。"
        >
          <textarea
            aria-label={`第 ${number} 镜关键帧提示词`}
            rows={14}
            maxLength={12000}
            value={prompt}
            disabled={busy}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </Field>
        <p className="quiet">
          本次画面修改会随关键帧审批传给后续视频。视频保留剧本中的动作时间轴和对白；若要改剧情动作或台词，请在分镜中编辑。
        </p>
        <p className="quiet">
          提交会发起新的图片生成；完成后仍需你审批。尚未生成的其他镜头会随后继续。
        </p>
        {paused && <p className="info-box">流程已暂停，请先关闭此窗口并继续流程，再提交修改。</p>}
        {error && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="dialog-actions">
        <button className="button secondary" disabled={busy} onClick={close}>
          取消
        </button>
        <button
          className="button primary"
          disabled={busy || paused || !prompt.trim() || !changed}
          onClick={() => void submit(prompt.trim())}
        >
          {busy ? '正在提交…' : '用修改后的提示词重新生成'}
        </button>
      </div>
    </Dialog>
  );
}

export function ConsistencyReports({
  reports,
  document,
}: {
  reports: { shotId: string; report: StudioTask['result'] & { references?: Reference[] } }[];
  document: Schema<'ProductionDraft'>;
}) {
  return (
    <div>
      {reports.length === 0 ? (
        <p className="info-box">
          这组旧素材尚未进行定妆图对照检查。可以重新制作关键帧后生成新候选；已有视频保留。
        </p>
      ) : (
        reports.map(({ shotId, report }) => (
          <details className="novel-details" key={shotId}>
            <summary>
              镜头 {document.shots.findIndex((s) => s.id === shotId) + 1} · 人物与构图检查 ·{' '}
              {report.visualReview?.findings.length ? '有提示，请核对' : '所检画面未发现明显问题'}
            </summary>
            <p>{report.visualReview?.summary}</p>
            <References items={report.references ?? []} />
            <div className="review-samples">
              {report.samples?.map((sample) => (
                <figure key={sample.index}>
                  <img src={sample.url} alt={`检查样本 ${sample.index}`} loading="lazy" />
                  <figcaption>{sample.atSeconds ?? 0} 秒</figcaption>
                </figure>
              ))}
            </div>
            {report.visualReview?.findings.map((finding, i) => (
              <p key={i}>
                <strong>{finding.description}</strong>
                <br />
                {finding.suggestion}
              </p>
            ))}
            <p className="quiet">模型只检查少量静帧；声音、口型及完整运动仍需你播放审阅。</p>
          </details>
        ))
      )}
    </div>
  );
}
